import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiChevronLeft, FiChevronRight, FiImage, FiZoomIn } from "react-icons/fi";
import { galleryAPI } from "../services/api";
import { resolveImageUrl } from "../utils/helpers";
import { useDataRefresh } from "../hooks/useDataRefresh";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { useSiteSettings } from "../context/SiteSettingsContext";

const CATEGORIES = ["All", "Campus", "Events", "Sports", "Academic", "Cultural", "Other"];

export default function Gallery() {
  const { settings } = useSiteSettings();
  const [filtered, setFiltered] = useState([]);
  const [category, setCategory] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const { data: images = [], loading } = useDataRefresh(
    async () => {
      const res = await galleryAPI.getAll({ limit: 1000 });
      return res?.data?.data?.images || [];
    },
    "gallery",
    { pollInterval: 10000 },
  );

  useEffect(() => {
    setFiltered(category === "All" ? images : images.filter((i) => i.category === category));
  }, [category, images]);

  const closeLightbox = () => setLightbox(null);
  const prevImage = () => setLightbox((l) => (l > 0 ? l - 1 : filtered.length - 1));
  const nextImage = () => setLightbox((l) => (l < filtered.length - 1 ? l + 1 : 0));

  useEffect(() => {
    const handleKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "ArrowLeft")  prevImage();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "Escape")     closeLightbox();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox]);

  return (
    <div className="bg-white min-h-screen">

      {/* ── Hero ── */}
      <div className="bg-[#1e3a8a] py-24 lg:py-32 relative overflow-hidden">
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        {/* Bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />

        <div className="relative max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-1 rounded-full bg-sky-400" />
              <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Our Memories</p>
            </div>
            <h1 className="text-4xl sm:text-6xl font-bold text-white leading-tight mb-6">
              Photo <span className="text-sky-300">Gallery</span>
            </h1>
            <p className="text-blue-200/70 text-lg max-w-xl leading-relaxed">
              Explore the vibrant life at {settings?.schoolName || "Hillbert English Boarding School "} through our collection of photographs.
            </p>
          </motion.div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16 py-16 lg:py-24">

        {/* Category Filter Buttons */}
        <div className="flex items-center gap-2 mb-12 flex-wrap">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded-xl transition-all duration-200 ${
                category === cat
                  ? "bg-[#2563EB] text-white shadow-md shadow-blue-200"
                  : "bg-[#F0F4FF] text-slate-600 hover:bg-blue-100 hover:text-[#2563EB] border border-blue-100"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {/* Masonry Grid */}
            <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
              {filtered.map((image, idx) => (
                <motion.div
                  key={image._id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: idx * 0.03 }}
                  className="relative break-inside-avoid overflow-hidden rounded-2xl cursor-pointer group border border-blue-100 shadow-sm hover:shadow-lg transition-shadow duration-300"
                  onClick={() => setLightbox(idx)}
                >
                  {image.imageUrl ? (
                    <img
                      src={resolveImageUrl(image.imageUrl)}
                      alt={image.title}
                      className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="w-full bg-[#F0F4FF] flex items-center justify-center" style={{ height: `${140 + (idx % 3) * 60}px` }}>
                      <FiImage className="w-10 h-10 text-blue-200" />
                    </div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-[#1e3a8a]/85 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-3 rounded-2xl">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center transform scale-75 group-hover:scale-100 transition-transform duration-300">
                      <FiZoomIn className="w-5 h-5 text-white" />
                    </div>
                    <p className="text-white font-bold text-sm px-4 text-center transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      {image.title}
                    </p>
                    {image.category && (
                      <span className="text-sky-300 text-[10px] font-bold uppercase tracking-widest transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                        {image.category}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-24 bg-[#F0F4FF] rounded-3xl border border-blue-100">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <FiImage className="w-8 h-8 text-[#2563EB]" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">No images found for this category.</p>
                <p className="text-slate-400 text-sm mt-2">Try selecting a different category above.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Lightbox ── */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-[#172554]/95 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={closeLightbox}
          >
            {/* Close */}
            <button onClick={closeLightbox}
              className="absolute top-5 right-5 text-blue-200 hover:text-white p-2.5 bg-white/10 hover:bg-white/20 rounded-xl transition-all z-10">
              <FiX className="w-5 h-5" />
            </button>

            {/* Prev */}
            <button onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-blue-200 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all z-10 hidden sm:flex items-center justify-center">
              <FiChevronLeft className="w-6 h-6" />
            </button>

            {/* Next */}
            <button onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-5 top-1/2 -translate-y-1/2 text-blue-200 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all z-10 hidden sm:flex items-center justify-center">
              <FiChevronRight className="w-6 h-6" />
            </button>

            {/* Image */}
            <motion.div
              key={lightbox}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full"
            >
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#1e3a8a]/50 p-2">
                {filtered[lightbox]?.imageUrl ? (
                  <img
                    src={resolveImageUrl(filtered[lightbox].imageUrl)}
                    alt={filtered[lightbox].title}
                    className="w-full max-h-[70vh] object-contain rounded-xl"
                  />
                ) : (
                  <div className="w-full h-72 bg-[#172554] flex items-center justify-center rounded-xl">
                    <FiImage className="w-16 h-16 text-blue-700" />
                  </div>
                )}
              </div>

              {/* Caption */}
              <div className="text-center mt-5">
                <h3 className="text-white font-bold text-xl">{filtered[lightbox]?.title}</h3>
                {filtered[lightbox]?.description && (
                  <p className="text-blue-200/60 text-sm mt-2">{filtered[lightbox].description}</p>
                )}
                <div className="inline-flex items-center gap-2 mt-4 px-4 py-1.5 bg-white/10 rounded-full">
                  <span className="text-sky-300 text-xs font-bold uppercase tracking-widest">
                    {filtered[lightbox]?.category}
                  </span>
                  <span className="text-blue-300/40 text-xs">•</span>
                  <span className="text-blue-200/60 text-xs font-semibold">{lightbox + 1} / {filtered.length}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
